/**
 * @engine/core - SpriteSync
 *
 * Bridges Level GameObjects with `core.SpriteRenderer` components into
 * the SoA storages (SpriteRendererStorage + TransformStorage) so that
 * SpriteRenderSystem can read flat arrays for GPU-friendly rendering.
 *
 * Shared between editor and player — neither should duplicate this logic.
 *
 * @module @engine/core
 */

import type { Level } from './Level';
import type { GUID } from './types';
import type { SpriteHandle } from './SpriteRendererStorage';
import {
  getSpriteRendererStorage,
  INVALID_SPRITE_HANDLE,
} from './SpriteRendererStorage';
import { getTransformStorage } from './TransformStorage';
import { packColorABGR } from './ColorUtils';

// ============================================================================
// STATE
// ============================================================================

/** GUID → SpriteHandle (same handle indexes both SoA storages) */
const entityToHandle = new Map<GUID, SpriteHandle>();

/** SpriteHandle → sprite asset id (for texture resolution) */
const spriteAssetIds = new Map<SpriteHandle, string>();

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Get the sprite asset ID mapped to a given handle.
 * Used by TextureResolver implementations to map numeric IDs → asset GUIDs.
 */
export function getSpriteAssetId(handle: SpriteHandle): string | null {
  return spriteAssetIds.get(handle) ?? null;
}

/**
 * Get all sprite asset IDs currently referenced by synced entities.
 */
export function getActiveSpriteAssetIds(): Set<string> {
  return new Set(spriteAssetIds.values());
}

/**
 * Get the sprite handle (== transform handle) for a given entity GUID.
 */
export function getSpriteHandle(guid: GUID): SpriteHandle {
  return entityToHandle.get(guid) ?? INVALID_SPRITE_HANDLE;
}

/**
 * Synchronise all `core.SpriteRenderer` components from a Level into
 * the SoA storages (SpriteRendererStorage + TransformStorage).
 *
 * Call once per frame before `SpriteRenderSystem.render()`.
 */
export function syncSpritesToStorage(level: Level | null): void {
  if (!level) return;

  const sprites = getSpriteRendererStorage();
  const transforms = getTransformStorage();
  const seen = new Set<GUID>();

  for (const go of level.getAllObjects()) {
    const spriteData = go.getComponentData('core.SpriteRenderer');
    if (!spriteData) continue;

    seen.add(go.guid);

    // Allocate or reuse handle
    let h = entityToHandle.get(go.guid);
    if (h === undefined || !sprites.isValid(h)) {
      h = sprites.allocate();
      entityToHandle.set(go.guid, h);

      // Ensure TransformStorage has the same index
      while (!transforms.isValid(h)) {
        const t = transforms.allocate();
        if (t >= h) break;
      }
    }

    // --- Transform ---
    const t = go.transform;
    transforms.setPosition(h, t.position.x, t.position.y, 0);
    transforms.setRotation(h, (t.rotation * Math.PI) / 180);
    transforms.setScale(h, t.lossyScale.x, t.lossyScale.y);

    // --- Sprite reference ---
    const spriteId: string | null = spriteData.sprite || null;
    if (spriteId) {
      spriteAssetIds.set(h, spriteId);
      sprites.setTextureId(h, h + 1); // 0 = no texture
    } else {
      spriteAssetIds.delete(h);
      sprites.setTextureId(h, 0);
    }

    // --- Visual properties ---
    sprites.setColor(h, packColorABGR(spriteData.color));
    sprites.setPivot(h, spriteData.pivotX, spriteData.pivotY);
    sprites.setRegion(
      h,
      spriteData.regionU0, spriteData.regionV0,
      spriteData.regionU1, spriteData.regionV1,
    );
    sprites.setSize(h, spriteData.width, spriteData.height);
    sprites.setSortingLayer(h, spriteData.sortingLayer);
    sprites.setOrderInLayer(h, spriteData.orderInLayer ?? 0);
    sprites.setEnabled(h, spriteData.visible !== false);
    sprites.setFlipX(h, spriteData.flipX);
    sprites.setFlipY(h, spriteData.flipY);
  }

  // Free removed entities
  for (const [guid, handle] of entityToHandle) {
    if (!seen.has(guid)) {
      sprites.free(handle);
      transforms.free(handle);
      entityToHandle.delete(guid);
      spriteAssetIds.delete(handle);
    }
  }
}

/**
 * Free all handles and clear bookkeeping.
 */
export function clearSpriteSync(): void {
  const sprites = getSpriteRendererStorage();
  const transforms = getTransformStorage();

  for (const handle of entityToHandle.values()) {
    if (sprites.isValid(handle)) sprites.free(handle);
    if (transforms.isValid(handle)) transforms.free(handle);
  }

  entityToHandle.clear();
  spriteAssetIds.clear();
}
